import { useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Center,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconShieldCheck } from "@tabler/icons-react";
import { ReportReviewMatrix } from "../../../components/admin/report-review/ReportReviewMatrix";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {
  useReportReviewSettings,
  useUpdateReportReviewSettings,
} from "../../../hooks/admin/useReportReviewSettings";
import {
  ReportReviewSettingsConflictError,
  type ReportReviewSettingsTarget,
} from "../../../services/admin/reportReviewSettings.service";

interface MatrixChange {
  target: ReportReviewSettingsTarget;
  reviewRequired: boolean;
}

export default function ReportReviewSettingsAdminPage() {
  const allowed = useHasPermission("report_review_settings.manage");
  const query = useReportReviewSettings(allowed);
  const update = useUpdateReportReviewSettings();
  const matrix = query.data;
  const refetchMatrix = query.refetch;
  const updateMatrix = update.mutate;
  const lastUpdatedAt = useMemo(
    () => (matrix ? new Date(matrix.updatedAt).toLocaleString() : ""),
    [matrix]
  );
  const handleRetry = useCallback(() => void refetchMatrix(), [refetchMatrix]);
  const handleUpdate = useCallback(
    (change: MatrixChange) => {
      if (!matrix) return;
      updateMatrix({ ...change, expectedRevision: matrix.revision });
    },
    [matrix, updateMatrix]
  );

  if (!allowed) {
    return (
      <Alert
        icon={<IconAlertCircle />}
        color="red"
        variant="light"
        title="Permission required"
        role="alert"
      >
        Only an administrator or Medical Director with report-review settings
        permission can open this page.
      </Alert>
    );
  }
  if (query.isLoading)
    return (
      <Center mih={320}>
        <Loader />
      </Center>
    );
  if (query.error || !matrix) {
    return (
      <Alert
        icon={<IconAlertCircle />}
        color="red"
        variant="light"
        title="Requirements unavailable"
        role="alert"
      >
        The matrix could not be loaded.
        <Button mt="sm" onClick={handleRetry}>
          Retry
        </Button>
      </Alert>
    );
  }
  if (matrix.prefixes.length === 0 || matrix.pathologists.length === 0) {
    return (
      <Alert
        icon={<IconShieldCheck />}
        color="blue"
        variant="light"
        title="No active combinations"
      >
        Add an active prefix and signing pathologist before configuring review
        requirements.
      </Alert>
    );
  }

  return (
    <Stack>
      <Title order={1}>Report Review Requirements</Title>
      <Text>
        Report review is required by default. New prefixes and pathologists also
        start with review required.
      </Text>
      {update.error instanceof ReportReviewSettingsConflictError && (
        <Alert
          icon={<IconAlertCircle />}
          color="yellow"
          variant="light"
          title="Settings changed"
          role="alert"
        >
          The matrix was refreshed because another update may have been saved.
          Review it and try again.
        </Alert>
      )}
      <ReportReviewMatrix
        matrix={matrix}
        disabled={update.isPending}
        onUpdate={handleUpdate}
      />
      <Text size="sm" c="dimmed">
        Last updated {lastUpdatedAt} by {matrix.updatedBy?.fullName || "system"}
        {" · "}
        Revision {matrix.revision}
      </Text>
    </Stack>
  );
}
